import { useEffect, useRef, useState } from 'react'
import type { VelticaTheme } from '@webbuilder/ui'
import { SignUp } from './Login'
import { MAX_PROMPT, MIN_PROMPT } from '../components/CreateWithAiModal/promptConfig'
import { MAX_SITE_DESIGN } from '../lib/siteDesign'
import { savePendingPrompt } from '../lib/pendingPrompt'
import { savePendingSiteDesign } from '../lib/siteDesign'
import { welcomeFrom } from '../lib/welcomeAnswers'
import { useI18n } from '../lib/i18n'

/**
 * Crear cuenta o entrar, dentro de la ventana que abre la caja de IA de
 * veltica.com (`/embed/auth`, ver `promptBox` en el worker).
 *
 * Quien escribió lo que quiere construir no sale a otra página a registrarse:
 * la ventana se abre sobre la que estaba leyendo, con el acceso de siempre
 * —correo y código, o Google—. Lo escrito llega por la dirección (`?start=`) o,
 * si esta página ya estaba cargada de antemano, por un mensaje de la página de
 * fuera (`veltica:auth-start`); se guarda (`pendingPrompt`) y, en cuanto hay
 * sesión, la pestaña entera va al panel, que sigue con la industria, lo que se
 * quiere del sitio y la generación (ver `Home`).
 *
 * Siempre en claro: va sobre la tarjeta blanca de esa ventana, sea cual sea el
 * tema que tenga guardado el panel.
 */
export function EmbedAuthPage({ hasSession, theme, onToggleTheme }: {
  hasSession: boolean
  theme: VelticaTheme
  onToggleTheme: () => void
}) {
  const { language, setLanguage, t } = useI18n()
  /* En el idioma de la página que la abre (`?lang=`), salvo que esta persona ya
     haya elegido el suyo en el panel: una ventana en inglés sobre una página en
     español se lee como otra web. */
  useEffect(() => {
    const wanted = new URLSearchParams(window.location.search).get('lang')
    if ((wanted !== 'es' && wanted !== 'en') || wanted === language) return
    let chosen = false
    try { chosen = /(?:^|; )veltica_language=/.test(document.cookie) || localStorage.getItem('veltica:language') !== null } catch { /* sin almacenamiento: no eligió nada */ }
    if (!chosen) setLanguage(wanted)
    // Una vez, al montar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* Si ya se pidió empezar: precargada, esta página espera sin hacer nada. */
  const [started, setStarted] = useState(false)
  const left = useRef(false)

  useEffect(() => {
    document.documentElement.classList.add('is-embed-auth')
    const frame = window.requestAnimationFrame(() => document.documentElement.setAttribute('data-theme', 'light'))
    return () => {
      window.cancelAnimationFrame(frame)
      document.documentElement.classList.remove('is-embed-auth')
    }
  }, [])

  useEffect(() => {
    /* Con lo que se trae: lo escrito en la caja de IA (`prompt`), o el diseño
       hecho con «Diseña esta página» (`design`, empaquetado; ver `siteDesign`). */
    /* Con lo escrito viajan, si se eligieron, el tipo de negocio y lo que se
       quiere del sitio (`brief`): cuentan como la bienvenida, así que quien
       los eligió allí no vuelve a contestarlos aquí. */
    const begin = (raw: unknown, design?: unknown, brief?: unknown) => {
      const prompt = typeof raw === 'string' ? raw.trim().slice(0, MAX_PROMPT) : ''
      if (prompt.length >= MIN_PROMPT) savePendingPrompt(prompt, welcomeFrom(brief))
      /* Generoso: con el ejemplo de la IA son ~15 000; el tope lo pone el guion de veltica.com (`MAX_PACKED`). */
      if (typeof design === 'string' && design.length <= MAX_SITE_DESIGN) savePendingSiteDesign(design)
      setStarted(true)
    }
    const query = new URLSearchParams(window.location.search)
    if (query.get('start') || query.get('design')) begin(query.get('start'), query.get('design'), query.get('brief'))
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; prompt?: unknown; design?: unknown; brief?: unknown } | null
      if (event.source === window.parent && data?.type === 'veltica:auth-start') begin(data.prompt, data.design, data.brief)
    }
    window.addEventListener('message', onMessage)
    /* Lista para recibirlo: el diseño ya no viaja en la dirección (no cabe), sino
       por mensaje, y la ventana de fuera espera a esto para mandarlo. */
    if (window.parent !== window) window.parent.postMessage({ type: 'veltica:auth-ready' }, '*')
    return () => window.removeEventListener('message', onMessage)
  }, [])

  /* El alto de lo que hay, a la ventana de fuera: ella se ajusta a esto en vez
     de dejar un hueco debajo o cortar el pie. Cada vez que cambia —del correo
     al código, un error, el aviso de idioma que se cierra—. */
  useEffect(() => {
    if (window.parent === window) return
    /* Con sesión no hay formulario que medir: lo de «Abriendo tu panel», que es bajo. */
    if (hasSession) {
      window.parent.postMessage({ type: 'veltica:auth-size', height: 260 }, '*')
      return
    }
    let observer: ResizeObserver | null = null
    const report = () => {
      const shell = document.querySelector<HTMLElement>('.login-form-shell')
      const section = shell?.closest<HTMLElement>('.login-form-section')
      if (!shell || !section) return
      const style = getComputedStyle(section)
      const height = shell.offsetHeight + (Number.parseFloat(style.paddingTop) || 0) + (Number.parseFloat(style.paddingBottom) || 0)
      window.parent.postMessage({ type: 'veltica:auth-size', height }, '*')
      if (!observer && typeof ResizeObserver !== 'undefined') {
        observer = new ResizeObserver(report)
        observer.observe(shell)
      }
    }
    /* El formulario se monta un momento después que esta página. */
    const frame = window.requestAnimationFrame(report)
    const retry = window.setTimeout(report, 400)
    return () => {
      window.cancelAnimationFrame(frame)
      window.clearTimeout(retry)
      observer?.disconnect()
    }
  }, [hasSession])

  /* Con sesión —ya la tenía, o acaba de entrar—: al panel, en la pestaña entera. */
  useEffect(() => {
    /* Fuera de la ventana incrustada —quien abre el enlace del correo en vez
       de escribir el código llega aquí en una pestaña— no hay nada que esperar:
       con sesión, al panel, que recoge lo que quedó guardado. */
    const standalone = window.top === window
    if ((!started && !standalone) || !hasSession || left.current) return
    left.current = true
    const destination = new URL('/', window.location.origin).toString()
    try {
      if (window.top && window.top !== window) window.top.location.href = destination
      else window.location.replace(destination)
    } catch {
      window.open(destination, '_blank', 'noopener')
    }
  }, [started, hasSession])

  /* Con sesión —quien ya tenía cuenta abierta en este navegador— la ventana
     está a la vista un instante mientras la pestaña va al panel. Vacía se leía
     como un fallo: dice lo que está pasando. */
  if (hasSession) {
    return (
      <div className="embed-auth-going" role="status">
        <span className="embed-auth-going-spin" aria-hidden="true" />
        <strong>{t('embedAuth.opening')}</strong>
        <small>{t('embedAuth.openingHint')}</small>
      </div>
    )
  }
  return <SignUp theme={theme} onToggleTheme={onToggleTheme} />
}
